import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import { Eye, EyeOff, Upload } from "lucide-react";

import logo from "../../Images/Logo.svg";
import RightArrow from "../../Images/Right Arrow.svg";
import defaultProfile from "../../Images/img.svg";
import ToastMessage from "../../config/ToastMessage";
import { API_URL } from "../../config/api";

const InstructorRegistration = () => {
  const navigate = useNavigate();
  const [bioText, setBioText] = useState("");
  const [formData, setFormData] = useState({
    first: "",
    middle: "",
    last: "",
    gender: "male",
    specialization: "",
    department: "",
    yearsOfExperience: "",
    linkedin: "",
    twitter: "",
    instagram: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "USA",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [mobile, setMobile] = useState("");
  const [alternateMobile, setAlternateMobile] = useState("");
  const [profileImage, setProfileImage] = useState(defaultProfile);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [videoUrl, setVideoUrl] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const CLOUDINARY_UPLOAD_PRESET =
    process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${API_URL}/location/states`);
        setStates(res.data.data || []);
      } catch (err) {
        console.error("Failed to load states");
      }
    };

    fetchStates();
  }, []);

  const handleStateSelect = async (e) => {
    const stateCode = e.target.value;

    setFormData((prev) => ({
      ...prev,
      state: stateCode,
      city: "",
    }));

    setCities([]);
    if (!stateCode) return;

    try {
      setLoadingCities(true);
      const res = await axios.get(
        `${API_URL}/location/states/${stateCode}/cities`,
      );
      setCities(res.data.data || []);
    } catch (err) {
      console.error("Failed to load cities");
    } finally {
      setLoadingCities(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpload = async (e, setUrlFunc) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`;

    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);

    try {
      setUploading(true);

      const res = await fetch(uploadUrl, {
        method: "POST",
        body: data,
      });

      const result = await res.json();

      if (result.secure_url) {
        setUrlFunc(result.secure_url);
        setToast({ type: "success", message: "Uploaded successfully!" });
      } else {
        console.error("Cloudinary error:", result);
        setToast({
          type: "error",
          message: result.error?.message || "Upload failed. Try again.",
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      setToast({ type: "error", message: "Upload failed. Please retry." });
    } finally {
      setUploading(false);
    }
  };
  console.log("Profile Image URL:", profileImage);
  console.log("Video URL:", videoUrl);
  const handleProfileUpload = (e) => handleUpload(e, setProfileImage);

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      handleUpload(e, setVideoUrl);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = {
      first: "First Name",
      last: "Last Name",
      gender: "Gender",
      specialization: "Specialization",
      department: "Department",
      yearsOfExperience: "Years of Experience",
      email: "Email",
      password: "Password",
      confirmPassword: "Re-enter Password",
      address: "Address",
      city: "City",
      state: "State",
      zip: "Zip Code",
      country: "Country",
      linkedin: "LinkedIn URL",
    };

    // Required field validation
    for (let field in requiredFields) {
      if (
        formData[field] === "" ||
        formData[field] === null ||
        formData[field] === undefined
      ) {
        newErrors[field] = `${requiredFields[field]} is required`;
      }
    }

    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password match validation
    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // ✅ Years of Experience validation (POSITIVE ONLY)
    if (formData.yearsOfExperience !== "") {
      const years = Number(formData.yearsOfExperience);

      if (isNaN(years)) {
        newErrors.yearsOfExperience = "Years of Experience must be a number";
      } else if (years < 0) {
        newErrors.yearsOfExperience =
          "Years of Experience must be a positive number";
      }
      // Optional: enforce minimum 1 year
      // else if (years === 0) {
      //   newErrors.yearsOfExperience =
      //     "Years of Experience must be greater than 0";
      // }
    }

    // Mobile validation
    if (!mobile) {
      newErrors.mobile = "Mobile number is required";
    }

    // Bio validation
    if (!bioText.trim()) {
      newErrors.bio = "Bio/Experience is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payload = {
      first_name: formData.first,
      middle_name: formData.middle || null,
      last_name: formData.last,
      gender: formData.gender,
      email: formData.email,
      password: formData.password,
      phone: mobile,
      alternate_phone: alternateMobile || null,
      specialization: formData.specialization,
      department: formData.department,
      years_of_experience: parseInt(formData.yearsOfExperience),
      bio: bioText,
      biovideo_url: videoUrl || null,
      linkedin_url: formData.linkedin || null,
      twitter_url: formData.twitter || null,
      instagram_url: formData.instagram || null,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      zip_code: formData.zip,
      avatar_url: profileImage !== defaultProfile ? profileImage : null,
    };

    try {
      setUploading(true);
      const res = await axios.post(
        `${API_URL}/user/create_instructor`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      setToast({
        type: "success",
        message: res.data?.message || "Instructor registered successfully!",
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      let errorMessage = "Failed to create account. Please try again.";
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === "string") errorMessage = data;
        else if (data.detail)
          errorMessage = Array.isArray(data.detail)
            ? data.detail.map((e) => e.msg || e).join(", ")
            : data.detail;
        else if (data.message) errorMessage = data.message;
        else if (data.error) errorMessage = data.error;
      } else if (err.message) errorMessage = err.message;
      setToast({ type: "error", message: errorMessage });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {toast.message && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ type: "", message: "" })}
        />
      )}

      <div className="w-full h-12 bg-white border-b border-gray-200 flex items-center px-10">
        <img className="w-36 h-10" src={logo} alt="Logo" />
      </div>

      <div className="max-w-6xl mx-auto px-8 py-8">
        <h1 className="text-3xl font-bold text-zinc-800 text-center mb-8">
          Registration
        </h1>

        {/* <div className="flex justify-center mb-10">
          <div className="w-96 h-14 rounded-full border border-zinc-200 flex items-center p-1">
            <button
              onClick={() => navigate("/student-signup")}
              className="flex-1 h-10 rounded-full transition-all font-normal hover:bg-gray-50"
            >
              Student
            </button>
            <button className="flex-1 h-10 rounded-full transition-all bg-cyan-100 font-semibold">
              Instructor
            </button>
          </div>
        </div> */}

        <form onSubmit={handleSubmit} className="space-y-10">
          {/* Personal Information */}
          <div className="flex gap-6 items-start">
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 bg-[#4AACC5] rounded-full mb-4 overflow-hidden flex items-center justify-center">
                {profileImage !== defaultProfile ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img src={defaultProfile} alt="" />
                )}
              </div>
              <label className="w-32 px-3 py-2 bg-white rounded border border-[#4AACC5] text-[#4AACC5] font-semibold text-xs text-center cursor-pointer hover:bg-teal-50">
                Upload Photo
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfileUpload}
                  className="hidden"
                />
              </label>
            </div>

            <div className="flex-1">
              <h2 className="text-base font-medium text-zinc-800 mb-6 text-left">
                Personal Information
              </h2>
              <div className="grid grid-cols-3 gap-6 mb-6">
                {[
                  { label: "First Name", name: "first", required: true },
                  { label: "Middle Name", name: "middle", required: false },
                  { label: "Last Name", name: "last", required: true },
                ].map((field) => (
                  <div key={field.name}>
                    <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                      {field.label}{" "}
                      {field.required && (
                        <span className="text-red-600">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={`Enter ${field.label}`}
                      className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                        errors[field.name]
                          ? "border-red-500"
                          : "border-gray-200"
                      } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                    />
                    {errors[field.name] && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors[field.name]}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Gender <span className="text-red-600">*</span>
                </label>
                <div className="flex gap-4">
                  {["male", "female"].map((gender) => (
                    <label
                      key={gender}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="gender"
                        value={gender}
                        checked={formData.gender === gender}
                        onChange={handleChange}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-neutral-700 capitalize">
                        {gender}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.gender && (
                  <p className="text-xs text-red-600 mt-1">{errors.gender}</p>
                )}
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Professional Information */}
          <div>
            <h2 className="text-base font-medium text-zinc-800 mb-6 text-left">
              Professional Information
            </h2>
            <div className="grid grid-cols-3 gap-6 mb-6">
              {[
                {
                  label: "Specialization",
                  name: "specialization",
                  required: true,
                  type: "text",
                },
                {
                  label: "Department",
                  name: "department",
                  required: true,
                  type: "text",
                },
                {
                  label: "Years of Experience",
                  name: "yearsOfExperience",
                  required: true,
                  type: "number",
                },
              ].map((field) => (
                <div key={field.name}>
                  <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                    {field.label}{" "}
                    {field.required && <span className="text-red-600">*</span>}
                  </label>

                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name]}
                    min={field.name === "yearsOfExperience" ? 0 : undefined}
                    step={field.name === "yearsOfExperience" ? 1 : undefined}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow only positive values for Years of Experience
                      if (
                        field.name === "yearsOfExperience" &&
                        value !== "" &&
                        Number(value) < 0
                      ) {
                        return;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }));

                      if (errors[field.name]) {
                        setErrors((prev) => ({ ...prev, [field.name]: "" }));
                      }
                    }}
                    placeholder={`Enter ${field.label}`}
                    className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                      errors[field.name] ? "border-red-500" : "border-gray-200"
                    } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                  />

                  {errors[field.name] && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-3 gap-6 mb-6">
              {[
                { label: "LinkedIn URL", name: "linkedin", required: true },
                {
                  label: "Twitter (optional)",
                  name: "twitter",
                  required: false,
                },
                {
                  label: "Instagram (optional)",
                  name: "instagram",
                  required: false,
                },
              ].map((field) => (
                <div key={field.name}>
                  <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                    {field.label}{" "}
                    {field.required && <span className="text-red-600">*</span>}
                  </label>
                  <input
                    type="url"
                    name={field.name}
                    value={formData[field.name]}
                    onChange={handleChange}
                    placeholder={`Enter ${field.label}`}
                    className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                      errors[field.name] ? "border-red-500" : "border-gray-200"
                    } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                  />
                  {errors[field.name] && (
                    <p className="text-xs text-red-600 mt-1">
                      {errors[field.name]}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex gap-6 items-start">
              <div className="w-96">
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Upload Video Introduction (Optional)
                </label>
                <label className="w-full h-40 border-2 border-dashed border-stone-300 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-[#4AACC5] transition-colors">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Choose Video File
                  </span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </label>
                {videoFile && (
                  <p className="text-xs text-green-600 mt-2">
                    Video selected: {videoFile.name}
                  </p>
                )}
              </div>

              <div className="flex-1">
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Add your BIO/Experience{" "}
                  <span className="text-red-600">*</span>
                </label>
                <textarea
                  name="bio"
                  value={bioText}
                  onChange={(e) => {
                    setBioText(e.target.value);
                    if (errors.bio) setErrors((prev) => ({ ...prev, bio: "" }));
                  }}
                  placeholder="Write your BIO/ Experience as Text"
                  className={`w-full h-40 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.bio ? "border-red-500" : "border-gray-200"
                  } text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                />
                {errors.bio && (
                  <p className="text-xs text-red-600 mt-1">{errors.bio}</p>
                )}
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Contact Details */}
          <div>
            <h2 className="text-base font-medium text-zinc-800 mb-6 text-left">
              Contact Details
            </h2>
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Mobile <span className="text-red-600">*</span>
                </label>
                <PhoneInput
                  country={"us"}
                  value={mobile}
                  onChange={(value) => {
                    setMobile(value);
                    if (errors.mobile)
                      setErrors((prev) => ({ ...prev, mobile: "" }));
                  }}
                  inputStyle={{
                    width: "100%",
                    height: "36px",
                    borderRadius: "8px",
                    borderColor: errors.mobile ? "#ef4444" : "#d1d5db",
                  }}
                />
                {errors.mobile && (
                  <p className="text-xs text-red-600 mt-1">{errors.mobile}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Alternate Mobile
                </label>
                <PhoneInput
                  country={"us"}
                  value={alternateMobile}
                  onChange={setAlternateMobile}
                  inputStyle={{
                    width: "100%",
                    height: "36px",
                    borderRadius: "8px",
                    borderColor: "#d1d5db",
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-6">
              <div className="col-span-1">
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Address <span className="text-red-600">*</span>
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter Address"
                  className={`w-full h-16 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.address ? "border-red-500" : "border-gray-200"
                  } text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                />
                {errors.address && (
                  <p className="text-xs text-red-600 mt-1">{errors.address}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  State <span className="text-red-600">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleStateSelect}
                  className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.state ? "border-red-500" : "border-gray-200"
                  } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.state && (
                  <p className="text-xs text-red-600 mt-1">{errors.state}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  City <span className="text-red-600">*</span>
                </label>
                <select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  disabled={!formData.state || loadingCities}
                  className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.city ? "border-red-500" : "border-gray-200"
                  } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5] disabled:bg-gray-100`}
                >
                  <option value="">
                    {loadingCities ? "Loading cities..." : "Select City"}
                  </option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="text-xs text-red-600 mt-1">{errors.city}</p>
                )}
                {errors.city && (
                  <p className="text-xs text-red-600 mt-1">{errors.city}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Zip Code <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  placeholder="Enter Zip Code"
                  className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.zip ? "border-red-500" : "border-gray-200"
                  } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                />
                {errors.zip && (
                  <p className="text-xs text-red-600 mt-1">{errors.zip}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-neutral-800 mb-2 block text-left">
                  Country <span className="text-red-600">*</span>
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full h-9 px-3 py-2 bg-white rounded-lg shadow-sm border ${
                    errors.country ? "border-red-500" : "border-gray-200"
                  } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
                >
                  <option value="USA">USA</option>
                  <option value="India">India</option>
                  <option value="UK">UK</option>
                  <option value="Canada">Canada</option>
                </select>
                {errors.country && (
                  <p className="text-xs text-red-600 mt-1">{errors.country}</p>
                )}
              </div>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Email and Password */}
          <div className="grid grid-cols-3 gap-6">
            <div>
              <label className="text-sm font-bold text-zinc-800 mb-2 block text-left">
                Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter Email"
                className={`w-full h-9 px-3 py-2 bg-white rounded shadow-sm border ${
                  errors.email ? "border-red-500" : "border-zinc-200"
                } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
              />
              {errors.email && (
                <p className="text-xs text-red-600 mt-1">{errors.email}</p>
              )}
            </div>
            <div className="relative">
              <label className="text-sm font-bold text-zinc-800 mb-2 block text-left">
                Password <span className="text-red-600">*</span>
              </label>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter Password"
                className={`w-full h-9 px-3 py-2 bg-white rounded shadow-sm border ${
                  errors.password ? "border-red-500" : "border-zinc-200"
                } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-zinc-400"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {errors.password && (
                <p className="text-xs text-red-600 mt-1">{errors.password}</p>
              )}
            </div>
            <div className="relative">
              <label className="text-sm font-bold text-zinc-800 mb-2 block text-left">
                Re-enter Password <span className="text-red-600">*</span>
              </label>
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Enter Password"
                className={`w-full h-9 px-3 py-2 bg-white rounded shadow-sm border ${
                  errors.confirmPassword ? "border-red-500" : "border-zinc-200"
                } text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-9 text-zinc-400"
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {errors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col items-center gap-4">
            <button
              type="submit"
              disabled={uploading}
              className="w-64 px-5 py-2.5 bg-[#4AACC5] text-white rounded font-bold text-sm hover:bg-teal-500 transition-colors disabled:opacity-50"
            >
              {uploading ? "Uploading..." : "Signup"}
            </button>
            <div className="flex items-center gap-2 w-full max-w-md">
              <hr className="flex-1 border-zinc-200" />
              <span className="text-neutral-600 text-xs font-medium">
                Or continue with
              </span>
              <hr className="flex-1 border-zinc-200" />
            </div>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-64 px-5 py-2.5 bg-white border border-[#4AACC5] text-[#4AACC5] rounded font-bold text-sm hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
            >
              Login <img src={RightArrow} alt="Arrow" className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstructorRegistration;